import { arrayUnion, collection, doc, getDoc, getDocs, query, serverTimestamp, where, writeBatch } from 'firebase/firestore';
import { useCallback, useEffect, useState } from 'react';
import { db } from '.';
import { changeStamp, currentRole } from './venues';
import { COLLECTION } from '../helper/collections';
import { sundaysChange, venueSundaysFlag } from '../components/sundays';

// Billing B1.5: one Sunday choice per host account.
// users/{hostId}.sundays holds the agreement, the on-site contact and the history
// (private: only the host and admins can read users). Each of the host's venues
// gets only venue.sundays = { open, termsVersion }, which search and the calendar read.

export const getHostSundays = async (hostId) => {
  const snapshot = await getDoc(doc(db, COLLECTION.USERS, hostId));
  return (snapshot.exists() && snapshot.data().sundays) || null;
};

// action: 'open' (with contact), 'close', or 'contact' (change the contact only).
export const saveHostSundays = async ({ hostId, action, current, contact, byUserId }) => {
  const { fields, entry } = sundaysChange({ action, current, contact, byUserId });
  const batch = writeBatch(db);
  const userUpdate = {
    'sundays.history': arrayUnion(entry),
    'sundays.updatedAt': serverTimestamp()
  };
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined) {
      userUpdate[`sundays.${key}`] = value;
    }
  });
  batch.update(doc(db, COLLECTION.USERS, hostId), userUpdate);
  if (action !== 'contact') {
    const venues = await getDocs(query(collection(db, COLLECTION.VENUES), where('userId', '==', hostId)));
    const flag = venueSundaysFlag(fields.open);
    // Venue-search VS1.3: every venue write says who and when (firestore.rules).
    const stamp = changeStamp(await currentRole());
    venues.forEach((venue) => batch.update(venue.ref, { sundays: flag, ...stamp }));
  }
  await batch.commit();
  return { ...fields, history: [...(current?.history || []), entry] };
};

export const useHostSundays = (hostId) => {
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(Boolean(hostId));
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    if (!hostId) {
      setLoading(false);
      return () => {};
    }
    let active = true;
    setLoading(true);
    setError(false);
    getHostSundays(hostId)
      .then((value) => active && setRecord(value))
      .catch((e) => {
        console.error('Could not read the Sunday choice', e);
        if (active) setError(true);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [hostId]);

  useEffect(() => load(), [load]);

  return { record, setRecord, loading, error };
};
